<template>
  <v-app>
    <!-- Desktop Navigation Drawer (extracted component).
         Hidden while the media player dialog is open, so the player is a
         clean, chrome-free fullscreen experience. Also hidden while the
         standalone quick-play dialog's chrome is showing (not merely while
         it's open — once handed off to PiP, isEpisodePlayerDialogVisible
         goes false and this chrome reappears). -->
    <nav-drawer v-if="loggedIn && mdAndUp && !showPlayer && !isEpisodePlayerDialogVisible" />

    <!-- App Bar: extracted component with dynamic title/icon per route.
         Hidden while either player dialog's chrome is showing (see above). -->
    <app-bar v-if="!showPlayer && !isEpisodePlayerDialogVisible" />

    <!-- Centralized confirmation dialog: mounted once, used by all components via useConfirmDialog -->
    <confirm-dialog />

    <v-main>
      <!-- key=$route.fullPath forces component remount on every navigation,
           ensuring each view starts fresh (no stale state from previous visits) -->
      <router-view :key="$route.fullPath"></router-view>

      <v-dialog v-model="addUrlDialog" :fullscreen="smAndDown" :max-width="smAndDown ? undefined : '600'">
        <v-card>
          <v-toolbar density="compact" color="transparent">
            <template v-slot:prepend>
              <v-icon icon="mdi-link" class="ml-2"></v-icon>
            </template>
            <v-toolbar-title>Add URL</v-toolbar-title>
            <template v-slot:append>
              <v-btn icon="mdi-close" @click="addUrlDialog = false"></v-btn>
            </template>
          </v-toolbar>
          <add-episodes-view @close="addUrlDialog = false"></add-episodes-view>
        </v-card>
      </v-dialog>

    </v-main>

    <!-- Mobile Bottom Navigation Bar + "More" bottom sheet (extracted component).
         Hidden while either player dialog's chrome is showing (see above). -->
    <bottom-nav-bar v-if="loggedIn && !mdAndUp && !showPlayer && !isEpisodePlayerDialogVisible" />

    <!-- The playlist media player is NOT mounted here anymore — it's the
         dedicated /player route (rendered by <router-view> in <v-main> above;
         its position:fixed wrapper covers the viewport). `showPlayer` is now a
         route-derived computed (name === "player"), so the four chrome-hiding
         guards above still hide the app bar/nav while the player page shows. -->

    <!-- Standalone "Play Episode" quick-play dialog: separate from the
         playlist-based media player (the /player route). Mounted for the whole
         preparing/playing/pip span so its <video> element survives being
         handed off to Picture-in-Picture when closed; the component's own
         chrome-visible/chrome-hidden CSS class (not v-if/v-show) controls
         whether it visually covers the screen. -->
    <episode-player-dialog v-if="isEpisodePlayerOpen" />

    <!-- Persistent sideload upload progress. Lives HERE, not in SideloadView,
         so it survives navigating away from the upload page — the upload
         itself is owned by the useSideloadUpload singleton and keeps running
         regardless of which page is mounted, so its progress has to be
         visible from anywhere too. Sits above the bottom nav on mobile.
         Hidden while either player has the screen. -->
    <v-card
      v-if="uploadIsActive && !showPlayer && !isEpisodePlayerDialogVisible"
      class="sideload-progress-card"
      :class="{ 'sideload-progress-card--with-bottom-nav': loggedIn && !mdAndUp }"
      elevation="8"
    >
      <v-card-text class="py-2 px-3">
        <div class="d-flex align-center ga-2 mb-1">
          <v-icon
            :icon="uploadStatus === 'interrupted' ? 'mdi-pause-circle-outline' : 'mdi-file-upload-outline'"
            :color="uploadStatus === 'interrupted' ? 'warning' : 'primary'"
            size="small"
          />
          <span class="text-caption text-truncate flex-grow-1">{{ uploadFileName }}</span>
          <span class="text-caption text-medium-emphasis">{{ uploadPercentValue }}%</span>
        </div>
        <v-progress-linear
          :model-value="uploadPercentValue"
          :color="uploadStatus === 'interrupted' ? 'warning' : 'primary'"
          :indeterminate="uploadStatus === 'processing'"
          height="4"
          rounded
        />
        <!-- Interrupted is a RESUMABLE pause (the bytes are safe server-side),
             so the action offered is Resume, not a retry-from-scratch. -->
        <div v-if="uploadStatus === 'interrupted'" class="d-flex align-center ga-2 mt-1">
          <span class="text-caption text-warning flex-grow-1">{{ uploadErrorMessage }}</span>
          <v-btn size="x-small" variant="text" color="warning" @click="resumeUpload">Resume</v-btn>
          <v-btn size="x-small" variant="text" @click="cancelUpload">Cancel</v-btn>
        </div>
      </v-card-text>
    </v-card>
  </v-app>

  <v-snackbar
    v-model="snackbar.show"
    :color="snackbar.color"
    timeout="3000"
    min-height="68"
  >
    {{ snackbar.message }}
    <template v-slot:actions>
      <v-btn variant="text" @click="snackbar.show = false"> Close </v-btn>
    </template>
  </v-snackbar>
</template>

<script setup>
/**
 * App.vue - Script Section
 *
 * Root application component. Manages global layout and cross-cutting concerns:
 *
 * Navigation:
 * - Desktop: NavDrawer (permanent rail, icon-only by default, expandable)
 * - Mobile:  BottomNavBar (4-button bar + "More" bottom sheet)
 * - Both nav components expose identical functionality (same routes and admin actions)
 *
 * Global UI:
 * - AppBar with dynamic title/icon/actions per route
 * - Add URL dialog (v-dialog, triggered from AppBar or nav menus)
 * - MediaPlayer — the playlist player, now the /player route (opened via useMediaPlayer.openPlayer → router.push)
 * - ConfirmDialog (singleton, used by all components via useConfirmDialog)
 * - Global snackbar for notifications (driven by useAppMenu.snackbar)
 *
 * Auth:
 * - Watches visibility changes so PWA sessions are validated when returning from background
 * - Fetches destination podcasts on mount for the Move Episodes dropdown
 */

import { useRouter } from "vue-router";
import { watchEffect, watch } from "vue";
import { useDisplay } from "vuetify";
import { useLogin } from "./composables/useLogin.js";
import { useAppMenu } from "./composables/useAppMenu.js";
import { useMediaPlayer } from "./composables/useMediaPlayer.js";
import { useEpisodePlayer } from "./composables/useEpisodePlayer.js";
import { usePolling } from "./composables/usePolling.js";
import { useSideloadUpload } from "./composables/useSideloadUpload.js";
import { onMounted, onUnmounted } from "vue";
import {
  refreshDestinationPodcasts,
  getDownloadingCount,
} from "@/api/index.js";
import { api } from "./api/config.js";
import AppBar from "./components/AppBar.vue";
import AddEpisodesView from "./components/AddEpisodesView.vue";
import BottomNavBar from "./components/BottomNavBar.vue";
import NavDrawer from "./components/NavDrawer.vue";
import ConfirmDialog from "./components/ConfirmDialog.vue";
// MediaPlayer.vue is no longer mounted here — it's the /player route's
// component (see router/index.js), rendered by <router-view>.
import EpisodePlayerDialog from "./components/EpisodePlayerDialog.vue";

// Shared app menu state: snackbar, add URL dialog, nav drawer, downloading indicator
const { snackbar, addUrlDialog, navDrawer, navRail, downloadingCount } = useAppMenu();
// In-progress sideload upload (singleton — survives navigating away from the
// upload page, which is exactly why its progress UI is rendered here).
const {
  fileName: uploadFileName,
  percent: uploadPercentValue,
  status: uploadStatus,
  errorMessage: uploadErrorMessage,
  isActive: uploadIsActive,
  resumeUpload,
  cancelUpload,
} = useSideloadUpload();
// Auth state: login status, admin check, current user info
const { loggedIn, getRefreshToken } = useLogin();
// Whether the playlist player page is showing — a computed derived from the
// route (name === "player"; see useMediaPlayer.js). Used only to hide the app
// chrome (nav/app-bar/bottom-nav) while on the /player page; the player itself
// is now mounted by <router-view>, not here.
const { showPlayer } = useMediaPlayer();
// Standalone quick-play dialog visibility. isOpen spans preparing/playing/pip
// (drives the dialog's mount); isDialogVisible excludes 'pip' (drives hiding
// the app's own chrome, so it reappears once handed off to PiP).
const {
  isOpen: isEpisodePlayerOpen,
  isDialogVisible: isEpisodePlayerDialogVisible,
} = useEpisodePlayer();

const { mdAndUp, smAndDown } = useDisplay();
// On desktop: ensure drawer is always visible and in rail mode.
// When switching from mobile to desktop, this re-enables the drawer.
watchEffect(() => {
  if (mdAndUp.value) {
    navDrawer.value = true;
    navRail.value = true;
  }
});

const router = useRouter();

/**
 * Safety net: clear any stranded inline `overflow` on <html>/<body> when the
 * route changes.
 *
 * VPullToRefresh freezes the page while you pull by writing `overflow: hidden`
 * onto EVERY scrollable ancestor it finds — and getScrollParents walks all the
 * way up, so <html> is usually in that list. It undoes this in a watcher when
 * the pull settles, but if the pull never settles (you navigate away mid-pull,
 * or the view unmounts first) the lock is stranded on the document FOREVER.
 *
 * The symptom is easy to miss because it is invisible on the list views — they
 * scroll inside their own containers — and only shows up on pages that rely on
 * the DOCUMENT scrolling, like Settings, which then simply won't scroll at all.
 *
 * Clearing to '' restores the stylesheet's value and is safe next to Vuetify's
 * own scroll blocking: dialogs/overlays block via the .v-overlay-scroll-blocked
 * CLASS, never inline styles, so this can't unblock an open dialog.
 */
router.afterEach(() => {
  document.documentElement.style.overflow = "";
  document.body.style.overflow = "";
});

/**
 * checkAuthOnVisibility - Validates auth when the app returns from background.
 * Mobile PWAs can stay backgrounded for days; this ensures the session
 * is still valid when the user switches back. If no refresh token exists,
 * forces re-login. Otherwise makes a lightweight /auth/me call to trigger
 * the token refresh interceptor if the access token has expired.
 */
const checkAuthOnVisibility = async () => {
  if (document.visibilityState === "visible" && loggedIn.value) {
    const refreshToken = getRefreshToken();
    if (!refreshToken) {
      // No refresh token - force re-login
      snackbar.value = {
        show: true,
        color: "warning",
        message: "Session expired. Please log in again.",
      };
      router.push({ name: "login" });
      return;
    }

    // Validate session by making a lightweight API call
    try {
      await api.get("/auth/me");
    } catch (error) {
      // If auth fails, the interceptor will handle redirect to login
      console.log("Auth check failed on visibility change");
    }
  }
};

/**
 * fetchGlobalDownloadingCount - One poll tick for the AppBar's persistent
 * activity indicator. Deliberately has no try/catch of its own: usePolling
 * already logs a failed tick and keeps going on the next one, which is the
 * right behavior here — a single transient failure shouldn't blank out a
 * badge that's meant to be trustworthy background-truth wherever the user
 * currently is, and there's no per-view "give up after N failures" concern
 * the way the list-view status polls have (those stop themselves on error
 * because they're tied to data that page loaded; this one isn't tied to
 * anything view-specific).
 */
const fetchGlobalDownloadingCount = async () => {
  const { data } = await getDownloadingCount();
  downloadingCount.value = data.count;
};

/**
 * Polls /episodes/downloading-count every 15s, independent of which page is
 * open — unlike the three list views' own 5s status polls (ListLatestView /
 * ListEpisodesView / PlaylistView), which used to be the ONLY thing keeping
 * downloadingCount honest, and so only did that while their own view
 * happened to be mounted (reported directly: navigating to Sideload — or
 * Settings, or anywhere else — while something was downloading/uploading
 * showed no indicator at all, even though it also showed nothing on the
 * Status page for the same underlying reason). 15s rather than the list
 * views' 5s: this is a passive ambient badge nobody is staring at waiting
 * for a percentage to tick, unlike those pages' per-episode progress bars —
 * doesn't need the same freshness, and it runs for the entire session
 * regardless of page, so a longer interval keeps the steady-state polling
 * cost down. The three list views' own polls (ListLatestView /
 * ListEpisodesView / PlaylistView) no longer ALSO write downloadingCount —
 * they used to, each from its own already-loaded (and therefore PARTIAL:
 * just that one podcast's episodes, or just Latest, or just Playlist) list,
 * which raced against this poll's TRUE total on the same ref and made the
 * badge visibly flicker between a smaller wrong number and the right one
 * every few seconds while on any of those three pages. This poll is now the
 * sole writer.
 */
const { startPolling: startDownloadingCountPoll, stopPolling: stopDownloadingCountPoll } =
  usePolling(fetchGlobalDownloadingCount, 15000);

/**
 * onMounted - Fetches the podcast list for the "Move to podcast" dropdown
 * (the shared move dialog in EpisodeContextMenu.vue), starts the global
 * downloading-count poll, and registers the visibility change listener for
 * background auth checks.
 */
onMounted(async () => {
  // Only fetch podcasts / start polling if logged in - prevents 401 cycle on login page
  if (loggedIn.value) {
    await refreshDestinationPodcasts();
    startDownloadingCountPoll();
  }
  // await startSchedulerOnServer();

  // Listen for visibility changes (app coming back from background)
  document.addEventListener("visibilitychange", checkAuthOnVisibility);
});

// When the user logs in after mount (e.g., login page → authenticated),
// populate the destination podcasts list for the Move Episodes dialog and
// start the global downloading-count poll. On logout, stop polling (no
// point hitting an endpoint that will just 401) and clear the badge
// immediately rather than leaving the last logged-in count showing.
watch(loggedIn, async (isLoggedIn) => {
  if (isLoggedIn) {
    await refreshDestinationPodcasts();
    startDownloadingCountPoll();
  } else {
    stopDownloadingCountPoll();
    downloadingCount.value = 0;
  }
});

// Cleanup: remove visibility change listener when app unmounts
onUnmounted(() => {
  document.removeEventListener("visibilitychange", checkAuthOnVisibility);
});
</script>

<style scoped>
/* Mobile toolbar height now controlled by height prop on AppBar component */

/* Persistent sideload upload progress. Fixed to the bottom so it stays put
   across navigation (the upload it reports on is a singleton that outlives
   any single page). z-index sits above page content but below Vuetify's
   overlay layer (2400+), so dialogs and the players still cover it. */
.sideload-progress-card {
  position: fixed;
  left: 8px;
  right: 8px;
  bottom: 8px;
  z-index: 2000;
  max-width: 560px;
  margin: 0 auto;
}

/* Clear the mobile bottom nav (56px) so the card isn't hidden behind it. */
.sideload-progress-card--with-bottom-nav {
  bottom: 64px;
}
</style>

<style>
/* Floating bottom-sheet menus. Global, since the sheet content is teleported
   to the document body and scoped styles cannot reach it. Targets Vuetifys
   own content element with matching specificity plus important so it beats
   the framework layer rules that pin the sheet flush, full width, square.

   Note: Vuetify positions the sheet content with position:absolute and
   JS-computed inline top/bottom, and actively cancels any margin-bottom — so to
   float it we override that positioning directly: pin to the bottom with a 12px
   gap (plus the home-indicator inset) and let the height be natural (top:auto). */
.v-bottom-sheet > .v-bottom-sheet__content.v-overlay__content {
  width: 90% !important;
  max-width: 640px !important;
  margin-inline: auto !important;
  border-radius: 24px !important;
  overflow: hidden !important;
  top: auto !important;
  bottom: calc(12px + env(safe-area-inset-bottom)) !important;
}


/* Keep the page area a stable, full-viewport height.
 *
 * Two jobs:
 *
 * 1. No collapse while loading. Every list view renders nothing during its
 *    fetch — both top-level branches are gated on `!loading` — so right after a
 *    tab switch the body is briefly EMPTY and the document would shrink, making
 *    the page visibly jump. A min-height keeps it steady either way.
 *
 * 2. Give iOS something to scroll. vh is the LARGE viewport (Safari's chrome
 *    collapsed), so with the chrome showing the page is taller than the visible
 *    area by exactly the chrome's height. One swipe scrolls that away, Safari
 *    collapses, and the layout then matches the viewport exactly. With dvh the
 *    page fits the visible area perfectly, nothing can scroll, and the chrome
 *    stays open permanently.
 *
 * The value matches what a loaded page occupies, so nothing shifts:
 *   app-bar padding (64) + list container (100vh - 120) + nav padding (56)
 *
 * NOTE: do NOT add a manual spacer for the bottom nav. v-bottom-navigation is a
 * Vuetify LAYOUT ITEM (useLayoutItem → --v-layout-bottom) and .v-main already
 * applies `padding-bottom: var(--v-layout-bottom)`, so the space is reserved
 * automatically. A hand-rolled spacer double-counts it and makes every page
 * overflow by exactly one nav height — a blank strip above the nav plus stray
 * scroll, which also masked the vh/dvh behaviour above for a long time.
 */
.v-main {
  min-height: 100vh;
}
</style>

